import { select, Selection } from 'd3-selection'

// Core
import { ComponentCore } from '@/core/component'

// Types
import { Sizing } from '@/types/component'
import { Spacing } from '@/types/spacing'

// Utils
import { isEqual, clamp, merge } from '@/utils/data'
import { getPixelValue } from '@/utils/misc'
import { ResizeObserver } from '@/utils/resize-observer'

// Config
import { ContainerDefaultConfig, ContainerConfigInterface } from './config'

export class ContainerCore {
  public svg: Selection<SVGSVGElement, unknown, null, undefined>
  public element: SVGSVGElement
  public prevConfig: ContainerConfigInterface
  public config: ContainerConfigInterface

  protected _defaultConfig: ContainerConfigInterface = ContainerDefaultConfig
  protected _container: HTMLElement
  protected _renderAnimationFrameId: number
  protected _isFirstRender = true
  protected _resizeObserver: ResizeObserver | undefined
  protected _resizeObserverAnimationFrameId: number
  protected _svgDefs: Selection<SVGDefsElement, unknown, null, undefined>
  protected _svgDefsExternal: Selection<SVGDefsElement, unknown, null, undefined>
  private _containerSize: { width: number; height: number }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  static DEFAULT_CONTAINER_HEIGHT = 300

  constructor (element: HTMLElement) {
    this._renderAnimationFrameId = null
    this._container = element

    // Setting `role` attribute to `image` to make the container accessible
    const container = select(this._container)
    container.attr('role', 'figure')

    // Create SVG element for visualizations
    this.svg = container.append('svg')
      // We set `display` to `block` because inline elements have an invisible
      //   inline space that adds 4px to the height of the container
      .style('display', 'block')
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('height', ContainerCore.DEFAULT_CONTAINER_HEIGHT) // Overriding default SVG height of 150
      .attr('aria-hidden', true)

    this._svgDefs = this.svg.append('defs')
    this._svgDefsExternal = this.svg.append('defs')
    this.element = this.svg.node()
  }

  public updateContainer<T extends ContainerConfigInterface> (config: T): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    this.prevConfig = this.config
    this.config = merge(this._defaultConfig, config)

    // Add `svgDefs` if provided in the config
    if (config?.svgDefs !== this.prevConfig?.svgDefs) {
      this._svgDefsExternal.selectAll('*').remove()
      this._svgDefsExternal.html(config.svgDefs)
    }
  }

  // The `_preRender` step should be used to perform some actions before rendering.
  // For example, calculating scales, setting component sizes, etc ...
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _preRender (): void {}

  /** Propagates the container's size, margin, and color function to the provided components.
   * Containers call this during `_preRender` */
  protected _propagateSizeAndStyleToComponents (components: (ComponentCore<unknown> | undefined)[], margin: Spacing): void {
    for (const c of components) {
      if (!c) continue
      c.setSize(this.width, this.height, this.containerWidth, this.containerHeight)
      c.setContainerMargin(margin)
      c.setColorFunction(this.config.colorFunction)
    }
  }

  // The `_render` step should be used to perform the actual rendering
  protected _render (duration?: number): void {
    const { config } = this

    // Apply the `aria-label` attribute
    select(this._container)
      .attr('aria-label', config.ariaLabel)

    this._isFirstRender = false
  }

  // Warning: Some Containers (i.e. Single Container) may override this method, so if you introduce any changes here,
  // make sure to check that other containers didn't break after them.
  public render (duration = this.config.duration): void {
    const width = this.config.width || this.containerWidth
    const height = this.config.height || this.containerHeight

    // We set SVG size in `render()` instead of `_render()`, because the size values in pixels will become
    // available only in the next animation when being accessed via `element.clientWidth` and `element.clientHeight`,
    // and we rely on those values when setting width and size of the components.
    this.svg
      .attr('width', width)
      .attr('height', height)

    // Set up Resize Observer. We do it in `render()` to capture container size change if it happened
    // in the next animation frame after the initial `render` was called.
    if (!this._resizeObserver) this._setUpResizeObserver()

    // Schedule the actual rendering in the next frame
    cancelAnimationFrame(this._renderAnimationFrameId)
    this._renderAnimationFrameId = requestAnimationFrame(() => {
      this._preRender()
      this._render(duration)
    })
  }

  get containerWidth (): number {
    // Falling back to the configured value when `clientWidth` is 0 (e.g. detached
    // containers or non-layouting environments like jsdom / SSR)
    return this.config.width
      ? (this.element.clientWidth || getPixelValue(this.config.width) || 0)
      : (this._container.clientWidth || this._container.getBoundingClientRect().width)
  }

  get containerHeight (): number {
    return this.config.height
      ? (this.element.clientHeight || getPixelValue(this.config.height) || 0)
      : (this._container.clientHeight || this._container.getBoundingClientRect().height || ContainerCore.DEFAULT_CONTAINER_HEIGHT)
  }

  get width (): number {
    return clamp(this.containerWidth - this.config.margin.left - this.config.margin.right, 0, Number.POSITIVE_INFINITY)
  }

  get height (): number {
    return clamp(this.containerHeight - this.config.margin.top - this.config.margin.bottom, 0, Number.POSITIVE_INFINITY)
  }

  protected _removeAllChildren (): void {
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild)
    }
  }

  /** Reconciles the direct children of the container's SVG element with the provided list:
   * only inserts / moves elements that are missing or out of order and removes the ones
   * that are no longer present. Unlike a full detach + re-append, this doesn't interrupt
   * CSS transitions or reset the internal state of unaffected elements */
  protected _reconcileChildren (desiredChildren: (Element | null | undefined)[]): void {
    const desired = desiredChildren.filter(Boolean)
    const desiredSet = new Set(desired)

    // Remove the children that are no longer present
    let child = this.element.firstChild
    while (child) {
      const next = child.nextSibling
      if (!desiredSet.has(child as Element)) this.element.removeChild(child)
      child = next
    }

    // Insert or move elements only when their current position doesn't match the desired one
    let cursor = this.element.firstChild
    for (const el of desired) {
      if (el === cursor) {
        cursor = cursor.nextSibling
      } else {
        this.element.insertBefore(el, cursor)
      }
    }
  }

  protected _onResize (): void {
    const { config } = this
    const redrawOnResize = config.sizing === Sizing.Fit || config.sizing === Sizing.FitWidth
    if (redrawOnResize) this.render(0)
  }

  protected _setUpResizeObserver (): void {
    if (this._resizeObserver) return

    const containerRect = this._container.getBoundingClientRect()
    this._containerSize = { width: containerRect.width, height: containerRect.height }

    this._resizeObserver = new ResizeObserver((entries, observer) => {
      // Using request animation frame to avoid multiple resize events when scrollbars appear/disappear
      // See more: https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_errors
      cancelAnimationFrame(this._resizeObserverAnimationFrameId)
      this._resizeObserverAnimationFrameId = requestAnimationFrame(() => {
        const resizedContainerRect = this._container.getBoundingClientRect()
        const resizedContainerSize = { width: resizedContainerRect.width, height: resizedContainerRect.height }
        const hasSizeChanged = !isEqual(this._containerSize, resizedContainerSize)
        // Do resize only if element is attached to the DOM
        // will come in useful when some ancestor of container becomes detached
        if (hasSizeChanged && resizedContainerSize.width && resizedContainerSize.height) {
          this._containerSize = resizedContainerSize
          this._onResize()
        }
      })
    })
    this._resizeObserver.observe(this._container)
  }

  public destroy (): void {
    cancelAnimationFrame(this._renderAnimationFrameId)
    cancelAnimationFrame(this._resizeObserverAnimationFrameId)
    this._resizeObserver?.disconnect()
    this.svg.remove()
  }
}
